import React, { ReactElement, memo, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import ErrorTypography from './ErrorTypography'
import Progress from './Progress'
import { RetrievableProps } from './Retrievable.types'

function Retrievable<T>({
    getPromise,
    setData,
    builder,
    keepContentOnReload = false,
    reloadDelayMs = 300,
}: RetrievableProps<T>): ReactElement {
    const [status, setStatus] = useState<'loading' | 'loaded' | Error>(
        'loading'
    )
    // Whether the delayed "refreshing" treatment (dim) is currently visible.
    const [reloading, setReloading] = useState(false)
    const loadedOnce = useRef(false)
    // Monotonic id so a stale (out-of-order) response can't overwrite the
    // result of a newer request.
    const requestId = useRef(0)

    useEffect(() => {
        const id = ++requestId.current
        let delayTimer: ReturnType<typeof setTimeout> | undefined

        if (keepContentOnReload && loadedOnce.current) {
            // Keep current content mounted; only signal the refresh if the
            // request outlasts the delay window.
            delayTimer = setTimeout(() => setReloading(true), reloadDelayMs)
        } else {
            setStatus('loading')
        }

        getPromise()
            .then((t) => {
                if (id !== requestId.current) return
                setData(t)
                loadedOnce.current = true
                setStatus('loaded')
            })
            .catch((error) => {
                if (id !== requestId.current) return
                setStatus(error)
            })
            .finally(() => {
                if (delayTimer) {
                    clearTimeout(delayTimer)
                }
                if (id === requestId.current) {
                    setReloading(false)
                }
            })

        return () => {
            if (delayTimer) {
                clearTimeout(delayTimer)
            }
        }
        // eslint-disable-next-line
    }, [getPromise])

    if (status === 'loaded') {
        if (!keepContentOnReload) {
            return builder()
        }

        return (
            <Box
                sx={{
                    transition: 'opacity 150ms ease',
                    ...(reloading && {
                        opacity: 0.6,
                        pointerEvents: 'none',
                    }),
                }}
            >
                {builder()}
            </Box>
        )
    }

    if (status instanceof Error) {
        return <ErrorTypography text={status.message} />
    }

    return <Progress />
}

export default memo(Retrievable)
